import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import PlayerCard from './components/PlayerCard';
import CompareMode from './components/CompareMode';
import RecentSearches from './components/RecentSearches';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import Footer from './components/Footer';
import { fetchPlayerData } from './services/api';
import './App.css';

function App() {
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [compareMode, setCompareMode] = useState(false);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse recent searches:', e);
      }
    }
  }, []);

  // Save recent searches to localStorage
  const addToRecentSearches = (username, uuid) => {
    const newSearch = {
      username,
      uuid,
      timestamp: Date.now()
    };

    setRecentSearches(prev => {
      // Remove duplicate if exists
      const filtered = prev.filter(s => s.username.toLowerCase() !== username.toLowerCase());
      // Add to beginning, keep max 10
      const updated = [newSearch, ...filtered].slice(0, 10);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
  };

  // Handle search submission
  const handleSearch = async (username) => {
    if (!username.trim()) return;

    setLoading(true);
    setError(null);
    setPlayerData(null);

    try {
      const data = await fetchPlayerData(username);
      setPlayerData(data.player);
      addToRecentSearches(data.player.username, data.player.uuid);
    } catch (err) {
      setError(err.message || 'Failed to fetch player data');
    } finally {
      setLoading(false);
    }
  };

  // Clear current search
  const handleClear = () => {
    setPlayerData(null);
    setError(null);
  };

  // Remove from recent searches
  const removeFromRecent = (username) => {
    setRecentSearches(prev => {
      const updated = prev.filter(s => s.username !== username);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <Router>
      <div className="app">
        <Header onCompareToggle={() => setCompareMode(!compareMode)} compareMode={compareMode} />

        <main className="main-content">
          <Routes>
            <Route path="/" element={
              <>
                {/* Hero Section */}
                <motion.section
                  className="hero-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="hero-content">
                    <motion.h1
                      className="hero-title"
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 }}
                    >
                      <span className="gradient-text">Hypixel</span> Stats Checker
                    </motion.h1>
                    <motion.p
                      className="hero-subtitle"
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      Search any player to view their Bedwars, Skywars, and more stats
                    </motion.p>
                  </div>

                  <SearchBar
                    onSearch={handleSearch}
                    onClear={handleClear}
                    isLoading={loading}
                    hasResults={!!playerData}
                  />
                </motion.section>

                {/* Compare Mode Toggle */}
                <AnimatePresence>
                  {compareMode && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <CompareMode />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Loading State */}
                <AnimatePresence>
                  {loading && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <LoadingSpinner />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Error State */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                    >
                      <ErrorMessage message={error} onRetry={() => setError(null)} />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Player Data */}
                <AnimatePresence>
                  {playerData && !loading && (
                    <motion.div
                      initial={{ opacity: 0, y: 40 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -40 }}
                      transition={{ duration: 0.5 }}
                    >
                      <PlayerCard player={playerData} />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Recent Searches */}
                {!playerData && !loading && !error && recentSearches.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <RecentSearches
                      searches={recentSearches}
                      onSelect={handleSearch}
                      onRemove={removeFromRecent}
                    />
                  </motion.div>
                )}
              </>
            } />
          </Routes>
        </main>

        <Footer />
      </div>
    </Router>
  );
}

export default App;
